import { UUID } from '@/types/types';
import {
	Group,
	Mesh,
	Box3,
	Box3Helper,
	FrontSide,
	MeshStandardMaterial,
} from 'three';
import { AssetsLib } from '../utils/AssetsLib';
import { ProjectRenderer } from '../ProjectRenderer';
import { CollidableObjectSerializeData, RotationDirection } from '../types';
import { RenderUtils } from '../utils/RenderUtils';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const materialSuccess = new MeshStandardMaterial({
	color: RENDERER_CONFIG.COLORS.GREEN,
	transparent: true,
	opacity: 0.2,
	side: FrontSide,
});

const materialError = new MeshStandardMaterial({
	color: RENDERER_CONFIG.COLORS.RED,
	transparent: true,
	opacity: 0.2,
	side: FrontSide,
});

export abstract class CollidableObject {
	id: UUID;
	group = new Group();
	collisionsEnabled = true;
	protected _selected = false;
	protected assetsLib = AssetsLib.getInstance();
	isCorrect = true;
	protected collidedModules = new Set<UUID>();
	collisionZoneMesh!: Mesh;
	boundingBox!: Box3;
	boundingBoxHelper!: Box3Helper;

	constructor(protected renderer: ProjectRenderer, id: UUID) {
		this.id = id;

		this.renderer.project.add(this.group);
		this.group.position.setY(RENDERER_CONFIG.COLLIDABLE_MODULE_POS_Y);
	}

	get selected() {
		return this._selected;
	}

	set selected(value: boolean) {
		if (this._selected === value) return;

		this._selected = value;

		if (value) {
			this.renderer.transformController.setToModule(this);
			this.renderer.transformController.selectedModule = this;
		} else {
			this.renderer.transformController.detach();
			this.renderer.transformController.selectedModule = null;
		}
	}

	protected validateStatus() {
		const isColliding = !this.collidedModules.size;

		const needsUpdate = this.isCorrect !== isColliding;
		this.isCorrect = isColliding;

		if (needsUpdate) this.updateOuterShapeMaterial();
	}

	protected updateOuterShapeMaterial() {
		this.collisionZoneMesh.material = this.isCorrect
			? materialSuccess.clone()
			: materialError.clone();
	}

	getCollidedModules() {
		return Array.from(this.collidedModules);
	}

	addCollidedModules(...ids: UUID[]) {
		for (const id of ids) {
			this.collidedModules.add(id);
		}

		this.validateStatus();
	}

	removeCollidedModules(...ids: UUID[]) {
		for (const id of ids) {
			this.collidedModules.delete(id);
		}

		this.validateStatus();
	}

	rotate(direction: RotationDirection) {
		const angle =
			direction === RotationDirection.CLOCKWISE ? -Math.PI / 2 : Math.PI / 2;

		this.group.rotation.y += angle;
		this.group.updateMatrixWorld(true);

		this.updateBoundingBox();
		this.renderer.transformController.collisionHandler.checkCollisions(this);
	}

	updateBoundingBox() {
		this.boundingBox.setFromObject(
			this.collisionsEnabled ? this.collisionZoneMesh : this.group,
			true
		);
		this.boundingBox.expandByVector(
			RENDERER_CONFIG.COLLISIONS.BOX3_EXPAND_VECTOR
		);
	}

	dispose() {
		RenderUtils.disposeObject(this.group);
		RenderUtils.disposeObject(this.boundingBoxHelper);
	}

	loadFromData<T extends CollidableObjectSerializeData>(data: T) {
		this.isCorrect = data.isCorrect;
		this.group.position.setX(data.position[0]);
		this.group.position.setZ(data.position[1]);
		this.group.rotation.y = data.rotation;
		this.collidedModules = new Set(data.collidedModules);
		this.group.updateMatrixWorld(true);

		if (!data.collisionsEnabled) return;

		this.updateBoundingBox();
		this.updateOuterShapeMaterial();
	}
}
